import { Component } from '@theme/component';

const METAFIELD_NAMESPACE = 'custom';
const METAFIELD_KEY = 'wishlist';
const PROXY_BASE_URL = '/apps/wishlist';
const POPOVER_SPACING = 8;

class WishlistButton extends Component {
  requiredRefs = ['trigger'];

  get productGid() {
    return this.dataset.productGid;
  }

  get customerId() {
    return this.dataset.customerId;
  }

  get isAuthenticated() {
    return Boolean(this.customerId);
  }

  get isWishlisted() {
    return this.hasAttribute('data-wishlisted');
  }

  connectedCallback() {
    super.connectedCallback();
    this.refs.trigger.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.trigger.removeEventListener('click', this.#handleClick);
  }

  #handleClick = (event) => {
    if (!this.isAuthenticated) {
      event.preventDefault();
      this.#showLoginPopover();
      return;
    }

    event.preventDefault();
    this.#toggle();
  };

  #showLoginPopover() {
    const popover = document.getElementById('wishlist-login-popover');
    if (!popover) return;

    popover.showPopover();
    this.#positionPopover(popover);

    const onScroll = () => {
      popover.hidePopover();
    };

    window.addEventListener('scroll', onScroll, { once: true, passive: true });

    popover.addEventListener('toggle', (e) => {
      if (e.newState === 'closed') {
        window.removeEventListener('scroll', onScroll);
      }
    }, { once: true });
  }

  #positionPopover(popover) {
    const triggerRect = this.refs.trigger.getBoundingClientRect();

    popover.style.left = '0';
    popover.style.top = '0';
    const popoverRect = popover.getBoundingClientRect();

    const left = triggerRect.left + triggerRect.width / 2 - popoverRect.width / 2;
    const top = triggerRect.top - popoverRect.height - POPOVER_SPACING;

    const clampedLeft = Math.max(POPOVER_SPACING, Math.min(left, window.innerWidth - popoverRect.width - POPOVER_SPACING));

    popover.style.left = `${clampedLeft}px`;
    popover.style.top = `${top}px`;
  }

  async #toggle() {
    const adding = !this.isWishlisted;
    this.#setWishlistedState(adding);

    try {
      await this.#sendRequest(adding ? 'POST' : 'DELETE');
    } catch {
      this.#setWishlistedState(!adding);
      return;
    }

    if (!adding) this.#removeFromWishlistPage();
  }

  #removeFromWishlistPage() {
    const item = this.closest('[data-wishlist-item]');
    if (!item) return;

    const grid = item.closest('[data-wishlist-grid]');
    item.remove();

    if (grid && !grid.querySelector('[data-wishlist-item]')) {
      const emptyState = grid.parentElement?.querySelector('[data-wishlist-empty]');
      grid.remove();
      emptyState?.removeAttribute('hidden');
    }
  }

  #setWishlistedState(wishlisted) {
    if (wishlisted) {
      this.setAttribute('data-wishlisted', '');
    } else {
      this.removeAttribute('data-wishlisted');
    }

    const trigger = this.refs.trigger;
    trigger.setAttribute('aria-pressed', String(wishlisted));

    const iconWrapper = trigger.querySelector('.wishlist-button__icon');
    if (iconWrapper) {
      iconWrapper.innerHTML = wishlisted
        ? this.#filledHeartSvg()
        : this.#outlineHeartSvg();
    }
  }

  #buildUrl() {
    return `${PROXY_BASE_URL}/customers/${this.customerId}/wishlist`;
  }

  #buildPayload() {
    return JSON.stringify({
      metafield_namespace: METAFIELD_NAMESPACE,
      metafield_key: METAFIELD_KEY,
      metafield_value: this.productGid,
    });
  }

  async #sendRequest(method) {
    const response = await fetch(this.#buildUrl(), {
      method,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: this.#buildPayload(),
    });

    if (!response.ok) {
      throw new Error(`Wishlist ${method} failed: ${response.status}`);
    }

    return response.json();
  }

  #outlineHeartSvg() {
    return `<svg width="18" height="15" viewBox="0 0 18 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.56445 2.29395C11.2555 0.357654 14.5093 0.199159 16.1572 2.06934L16.3115 2.25684C17.6934 4.05242 17.4706 6.64438 16.1533 8.55859H16.1523C14.5814 10.8417 11.7734 12.7135 9 14.1553C6.22672 12.7136 3.41864 10.8416 1.84766 8.55957C0.571223 6.70455 0.321536 4.21413 1.56348 2.42773L1.68848 2.25684C3.27895 0.191491 6.69054 0.294181 8.43359 2.29395L8.99902 2.94141L9.56445 2.29395Z" stroke="currentColor" stroke-width="var(--icon-stroke-width)"/></svg>`;
  }

  #filledHeartSvg() {
    return `<svg width="18" height="15" viewBox="0 0 18 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.56445 2.29395C11.2555 0.357654 14.5093 0.199159 16.1572 2.06934L16.3115 2.25684C17.6934 4.05242 17.4706 6.64438 16.1533 8.55859H16.1523C14.5814 10.8417 11.7734 12.7135 9 14.1553C6.22672 12.7136 3.41864 10.8416 1.84766 8.55957C0.571223 6.70455 0.321536 4.21413 1.56348 2.42773L1.68848 2.25684C3.27895 0.191491 6.69054 0.294181 8.43359 2.29395L8.99902 2.94141L9.56445 2.29395Z" fill="currentColor" stroke="currentColor" stroke-width="var(--icon-stroke-width)"/></svg>`;
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}
